import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ping, publicCounts, friendCounts } from '../services/presence';
import { useAuth } from './AuthContext';

/*
 * PresenceContext — un seul boucle de présence partagée (toolbar + cartes de jeu).
 * - ping le serveur toutes les 30 s (marque le joueur en ligne sur "appstore") ;
 * - rafraîchit total + par jeu (public) et amis (si connecté) toutes les ~15 s.
 * Seuil d'affichage : on n'affiche un compteur qu'à partir de MIN joueurs (défaut 10) — en dessous,
 * mieux vaut ne rien montrer (0 ou 2 joueurs "en ligne" ne donne pas envie).
 */
const MIN = parseInt(process.env.REACT_APP_ONLINE_MIN, 10);
export const ONLINE_MIN = Number.isFinite(MIN) ? MIN : 10;

const Ctx = createContext({ total: 0, games: {}, friendsTotal: 0, friendsByGame: {}, min: ONLINE_MIN });

export function PresenceProvider({ children }) {
  const { auth, isLoggedIn } = useAuth();
  const [state, setState] = useState({ total: 0, games: {}, friendsTotal: 0, friendsByGame: {} });
  const [friendsOnline, setFriendsOnline] = useState(0);   // amis en ligne — compté CÔTÉ CLIENT (fiable)
  const alive = useRef(true);
  const friendIdsRef = useRef(new Set());   // uids de mes amis (pour ne compter QU'EUX dans les events de présence)
  const onlineRef = useRef(new Set());      // uids d'amis PRÉSENTS (source = ping serveur, robuste au socket)
  const lastSeenRef = useRef({});           // uid -> dernier instant vu présent (ping). Grâce 30 s → absorbe les polls ratés
  const lastRefreshAt = useRef(0);          // THROTTLE : borne le refresh (les notifs natives déclenchaient un refresh à chaque fois → lag)
  const lastSetupAt = useRef(0);            // THROTTLE : borne le re-fetch de la liste d'amis (listFriends)

  const refresh = useCallback(async () => {
    const _now = Date.now();
    if (_now - lastRefreshAt.current < 4000) return;   // max 1 refresh réseau / 4 s (poll 6 s OK ; absorbe les rafales de notifs)
    lastRefreshAt.current = _now;
    const [pub, fr] = await Promise.all([publicCounts(), isLoggedIn ? friendCounts() : Promise.resolve(null)]);
    if (!alive.current) return;
    setState((s) => ({
      total: pub.total || 0, games: pub.games || {},
      friendsTotal: fr ? fr.friends_total : (isLoggedIn ? s.friendsTotal : 0),
      friendsByGame: fr ? fr.friends_by_game : (isLoggedIn ? s.friendsByGame : {}),
    }));
    // SOURCE DE VÉRITÉ du « en ligne » : le PING serveur (fr.friends_online). ROBUSTESSE ANTI-CLIGNOTEMENT :
    //  • fr.friends_online === null → le RPC a ÉCHOUÉ (réseau/token) → on NE TOUCHE À RIEN (on garde l'état). C'était
    //    LA cause du clignotement : un poll raté vidait la liste.
    //  • sinon on HORODATE chaque ami présent (lastSeen = now), puis un ami est EN LIGNE s'il a été vu présent dans les
    //    30 dernières secondes → un poll isolé qui l'oublie ne le fait PAS disparaître. Hors ligne seulement après une
    //    absence SOUTENUE (le serveur l'a déjà retiré au bout de ~120 s de non-ping = onglet fermé / PC éteint / veille).
    if (fr && Array.isArray(fr.friends_online)) {
      const now = Date.now(), seen = lastSeenRef.current;
      fr.friends_online.forEach((id) => { if (friendIdsRef.current.size === 0 || friendIdsRef.current.has(id)) seen[id] = now; });
      const next = new Set();
      Object.keys(seen).forEach((id) => { if (now - seen[id] < 60000) next.add(id); else delete seen[id]; });
      onlineRef.current = next;
      setFriendsOnline(next.size);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    alive.current = true;
    ping('appstore');
    refresh();
    // Rafraîchissement plus FRÉQUENT (présence/amis quasi temps réel) : ping 15 s, compteurs 6 s.
    const pingIv = setInterval(() => ping('appstore'), 15000);
    const refIv = setInterval(refresh, 6000);
    return () => { alive.current = false; clearInterval(pingIv); clearInterval(refIv); };
  }, [refresh]);

  // Compteur d'amis EN LIGNE fiable (client) : on SUIT la présence de nos amis et on écoute les changements
  // de statut. Le RPC serveur (friendCounts) pouvait renvoyer 0 alors qu'un ami était bien connecté →
  // incohérence dans le drawer/menu. Ici c'est la même source que la liste « Amis en ligne ».
  useEffect(() => {
    if (!isLoggedIn || !auth || !auth.enabled) { onlineRef.current = new Set(); friendIdsRef.current = new Set(); setFriendsOnline(0); return undefined; }
    let live = true;
    // On récupère juste la LISTE d'amis (pour filtrer) ; le « en ligne » vient du poll PING (refresh, robuste).
    const setup = async () => {
      // THROTTLE : ne re-fetch la liste d'amis qu'au plus une fois / 8 s (sinon chaque notif native la re-fetchait → lag).
      if (Date.now() - lastSetupAt.current < 8000) { refresh(); return; }
      lastSetupAt.current = Date.now();
      const fr = await auth.listFriends(0);
      const fids = (fr || []).map((f) => f.user && (f.user.id || f.user.user_id)).filter(Boolean);
      friendIdsRef.current = new Set(fids);
      if (live) refresh();   // recalcule immédiatement l'ensemble présent (source ping)
    };
    setup();
    // Le socket sert UNIQUEMENT à l'ajout INSTANTANÉ (join, avant le prochain poll) — le `leave` est IGNORÉ : le
    // hors-ligne est décidé EXCLUSIVEMENT par l'expiration du ping (onglet fermé / PC éteint / veille). Un hoquet de
    // socket ne fait donc JAMAIS passer un ami hors ligne tant que son onglet AppStore est ouvert et son PC éveillé.
    const off = auth.onStatusPresence((ev) => {
      (ev.joins || []).forEach((p) => { if (friendIdsRef.current.has(p.user_id)) { lastSeenRef.current[p.user_id] = Date.now(); if (!onlineRef.current.has(p.user_id)) { onlineRef.current.add(p.user_id); setFriendsOnline(onlineRef.current.size); } } });
    });
    const offN = auth.onNotification ? auth.onNotification(() => setup()) : null;
    const offRe = auth.onReconnect ? auth.onReconnect(() => setup()) : null;
    return () => { live = false; if (off) off(); if (offN) offN(); if (offRe) offRe(); };
  }, [auth, isLoggedIn, refresh]);

  // On EXPOSE friendsTotal = compte client fiable (remplace le RPC serveur qui divergeait).
  return <Ctx.Provider value={{ ...state, friendsTotal: friendsOnline, friendsOnline, min: ONLINE_MIN }}>{children}</Ctx.Provider>;
}

export const usePresence = () => useContext(Ctx);
